import React, { useMemo, useState, useEffect } from "react";
import FillVendor from "./FillVendor";
import RecievedQuoatation from "./recievedquoatation";
import { Plus, Link, X, Copy, Check } from "lucide-react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { QuotationReceiptPDF } from "./PurchaseOrderReceipt";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import MultipleVendorsInvite from "./MultipleVendorsInvite";

const formatDisplayDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function QuotationSummary({ rfq, onClose, onProceedToPO }) {
  const [activeTab, setActiveTab] = useState("received");
  const [showFillVendor, setShowFillVendor] = useState(false);
  const [openReceivedQuotation, setOpenReceivedQuotation] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [shareFormData, setShareFormData] = useState({
    vendor_whatsapp_number: "",
    vendor_name: "",
    vendor_address: "",
    validity_days: 7,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationLink, setInvitationLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedExistingVendor, setSelectedExistingVendor] = useState(null);
  const [isExistingVendor, setIsExistingVendor] = useState(false);
  const [showMultipleVendorsInvite, setShowMultipleVendorsInvite] =
    useState(false);

  const { addAlert } = useAlert();

  // find frontend base url from window.location.origin
  const FRONTEND_BASE_URL = window.location.origin;

  const items = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, i) => ({
        id: i + 1,
        name: "Cement",
        variant: "Grade 50",
        qty: "50 bags",
      })),
    []
  );

  const getQuotations = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/applications/rfq/${rfq.id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error("Failed to fetch quotations");
      }
      const data = await res.json();
      console.log("Fetched Quotations:", data);
      setQuotations(data);
    } catch (err) {
      console.error("Error fetching quotations:", err);
    }
  };

  const getInvitations = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/get-rfq-invitations/${rfq.id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error("Failed to fetch invitations");
      }
      const data = await res.json();
      console.log("Fetched Invitations:", data);
      setInvitations(data);
    } catch (err) {
      console.error("Error fetching invitations:", err);
    }
  };

  const getVendors = async () => {
    try {
      const token = localStorage.getItem("access");
      const org_id = JSON.parse(localStorage.getItem("organization"))?.id;

      const response = await fetch(
        `${BASE_URL}/inventory/list-org-vendors/${org_id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.status && Array.isArray(result.data)) {
        setVendors(result.data);
      } else {
        console.error("Failed to load vendors", result.message);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handleShareLink = async () => {
    if (!shareFormData.vendor_whatsapp_number || !shareFormData.vendor_name) {
      addAlert("Please fill in vendor WhatsApp number and name", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        rfq: rfq.id,
        vendor_whatsapp_number: `+91${shareFormData.vendor_whatsapp_number}`,
        validity_days: shareFormData.validity_days,
        vendor_name: shareFormData.vendor_name,
      };

      if (shareFormData.vendor_address) {
        payload.vendor_address = shareFormData.vendor_address;
      }

      const res = await fetch(`${BASE_URL}/inventory/rfq-invitations/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        // setInvitationLink(data.data.invitation_link);
        addAlert(data.message, "success");
        getInvitations();
        getQuotations();
        handleCloseShareModal();
      } else if (data.error) {
        addAlert(data.error, "error");
      }
    } catch (err) {
      console.error("Error sharing link:", err);
      addAlert("Failed to send invitation", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(invitationLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setShareFormData({
      vendor_whatsapp_number: "",
      vendor_name: "",
      vendor_address: "",
      validity_days: 7,
    });
    setInvitationLink("");
    setLinkCopied(false);
  };

  useEffect(() => {
    if (rfq && rfq.id) {
      getQuotations();
      getInvitations();
      getVendors();
    }
  }, [rfq]);

  useEffect(() => {
    if (!shareFormData.vendor_whatsapp_number || vendors.length === 0) {
      setSelectedExistingVendor(null);
      setIsExistingVendor(false);
      return;
    }

    const inputNumber = shareFormData.vendor_whatsapp_number.trim();
    const normalizedInput = inputNumber.startsWith("+91")
      ? inputNumber
      : `+91${inputNumber.replace(/^\+91/, "")}`;

    const matchedVendor = vendors.find((v) => {
      const vendorNumber = v.contact_no?.trim();
      return (
        vendorNumber === normalizedInput ||
        vendorNumber === normalizedInput.replace(/^\+91/, "")
      );
    });

    if (matchedVendor) {
      setSelectedExistingVendor(matchedVendor);
      setIsExistingVendor(true);
      setShareFormData((prev) => ({
        ...prev,
        vendor_name: matchedVendor.name || "",
        vendor_address: matchedVendor.address || "",
      }));
    } else {
      setSelectedExistingVendor(null);
      setIsExistingVendor(false);
    }
  }, [shareFormData.vendor_whatsapp_number, vendors]);

  return (
    <div className="fixed right-0 top-16 w-1/2 h-[calc(100vh-4rem)] bg-white border-l border-gray-200 shadow-xl z-40 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div>
          <div className="text-base font-semibold text-gray-900">
            Quotation Summary
          </div>
          <div className="text-xs text-[#232323]">
            {rfq?.rfq_number} <span className="ml-2 mr-2">|</span>{" "}
            {rfq?.material_request_number}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* PDF download (Quotation receipt) */}
          <PDFDownloadLink
            document={<QuotationReceiptPDF rfq={rfq} />}
            fileName={`quotation-${rfq?.rfq_number || rfq?.id || "rfq"}.pdf`}
          >
            {({ loading }) => (
              <a
                className="flex justify-center gap-2 px-3 py-2 text-sm bg-white border-1 border-[#3B0069] text-[#3B0069] rounded-md hover:bg-gray-200"
                style={{
                  display: rfq?.status === "OPEN" ? "flex" : "none",
                }}
              >
                <span className="flex items-center"> {loading ? "Preparing..." : "Download Receipt"}</span>
              </a>
            )}
          </PDFDownloadLink>

          <button
            onClick={() => setShowShareModal(true)}
            style={{
              display: rfq?.status === "OPEN" ? "flex" : "none",
            }}
            className="flex justify-center gap-2 px-3 py-2 text-sm bg-[#3B0069] border rounded-md text-[#FFFFFF]"
          >
            <span className="flex items-center">
              <Link size={14} />
            </span>
            <span className="flex items-center">Share link</span>
          </button>
          <button
            onClick={() => setShowFillVendor(true)}
            style={{
              display: rfq?.status === "OPEN" ? "flex" : "none",
            }}
            className="flex justify-center gap-2 px-3 py-2 text-sm bg-[#FFFFFF] border-1 border-[#3B0069] text-[#3B0069] rounded-md hover:bg-gray-200"
          >
            <span className="flex items-center">
              <Plus size={14} />
            </span>
            <span className="flex items-center">Fill for Vendor</span>
          </button>
          <button
            onClick={onClose}
            className="ml-1 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600"
          >
            <X />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <div className="font-medium text-[#232323]">Content</div>
            <div>Total {rfq.lines.length} items</div>
          </div>

          <div className="space-y-2">
            {rfq.lines.map((it, index) => (
              <div
                key={it.id}
                className="border border-gray-200 rounded-lg px-3 py-2"
              >
                <div className="text-[13px] text-gray-500 mb-1 border-b">
                  Material {index + 1}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[16px] font-medium text-[#232323]">
                      {it.material_name}
                    </div>
                    <div className="text-[14px] text-gray-500">
                      {it.subcategory}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[16px] font-medium text-[#232323]">
                      {it.quantity} {it.uom}
                    </div>
                    <div className="text-[14px] text-gray-500">
                      Requested quantity
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-2">
          <div className="px-4">
            <div className="flex gap-6 border-b border-gray-200">
              <button
                className={`py-2 text-sm ${activeTab === "received"
                  ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                  : "text-gray-500"
                  }`}
                onClick={() => setActiveTab("received")}
              >
                Received
              </button>
              <button
                className={`py-2 text-sm ${activeTab === "requested"
                  ? "text-[#3B0069] border-b-2 border-[#3B0069]"
                  : "text-gray-500"
                  }`}
                onClick={() => setActiveTab("requested")}
              >
                Requested
              </button>
            </div>
          </div>

          {activeTab === "received" ? (
            <div className="px-4 py-3">
              <div className="text-[14px] text-gray-500 mb-3">
                {quotations.length} Received quotations
              </div>
              <div className="space-y-2">
                {quotations.map((q, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <div className="w-1/5">
                      <div className="text-[14px] text-gray-500">
                        Quotation amount
                      </div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        ₹ {q.total_price.toLocaleString()}
                      </div>
                    </div>
                    <div className="w-1/5">
                      <div className="text-[14px] text-gray-500">Vendor</div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        {q.vendor_name}
                      </div>
                    </div>
                    <div className="w-1/5">
                      <div className="text-[14px] text-gray-500">
                        Received on
                      </div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        {formatDisplayDate(q.created_on)}
                      </div>
                    </div>
                    <div className="w-1/5">
                      <div className="text-[14px] text-gray-500">
                        Expected delivery
                      </div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        {formatDisplayDate(q.delivery_date)}
                      </div>
                    </div>
                    <div className="w-1/5 flex justify-end items-center gap-2">
                      {q.status === "selected" && (
                        <span className="px-2 py-0.5 text-[10px] font-medium text-[#00C875] bg-white border border-[#ABEFC6] rounded-full whitespace-nowrap">
                          Selected
                        </span>
                      )}
                      {q.status === "rejected" && (
                        <span className="px-2 py-0.5 text-[10px] font-medium text-[#D30606] bg-white border border-[#D30606] rounded-full whitespace-nowrap">
                          Rejected
                        </span>
                      )}
                      {q.status === "pending_approval" && (
                        <span className="px-2 py-0.5 text-[10px] font-medium text-[#F0B41D] bg-white border border-[#FEDF89] rounded-full whitespace-nowrap">
                          Pending
                        </span>
                      )}
                      <button
                        onClick={() => setOpenReceivedQuotation(q)}
                        className="px-3 py-1 text-sm border-1 rounded-md text-[#3B0069] border-[#3B0069] hover:bg-[#F5EEF9] whitespace-nowrap"
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="px-4 py-3">
              <div className="text-[14px] text-gray-500 mb-3">
                {invitations.length} Sent quotations
              </div>
              <div className="space-y-2">
                {invitations.map((rq, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-5 px-3 py-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <div className="w-1/4">
                      <div className="text-[14px] text-gray-500">
                        Vendor Name
                      </div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        {rq.vendor_name}
                      </div>
                      <div className="text-[12px] text-gray-400">
                        {rq.vendor_phone}
                      </div>
                    </div>
                    <div className="w-1/4">
                      <div className="text-[14px] text-gray-500">RFQ ID</div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        {rfq.rfq_number}
                      </div>
                    </div>
                    <div className="w-1/4 text-center">
                      <div
                        className={`text-[14px] font-medium ${rq.status === "SENT"
                          ? "text-green-500 border-1 border-green-500 rounded-full px-2"
                          : rq.status === "IN_PROGRESS"
                            ? "text-blue-500 border-1 border-blue-500 rounded-full px-2"
                            : rq.status === "EXPIRED"
                              ? "text-red-500 border-1 border-red-500 rounded-full px-2"
                              : rq.status === "USED"
                                ? "text-purple-500 border-1 border-purple-500 rounded-full px-2"
                                : "text-gray-500"
                          }`}
                      >
                        {rq.status}
                      </div>
                    </div>
                    <div className="w-1/4 text-right flex flex-col items-end">
                      <div className="text-[14px] text-gray-500">
                        Created on
                      </div>
                      <div className="text-[16px] font-medium text-[#232323]">
                        {formatDisplayDate(rq.created_on)}
                      </div>
                      <div className="text-[12px] text-gray-400">
                        Expires: {formatDisplayDate(rq.expiry)}
                      </div>
                      {rq.status !== "EXPIRED" && rq.status !== "USED" && (
                        <button
                          onClick={() => {
                            const link = `${FRONTEND_BASE_URL}/quotations/${rq.token}`;
                            navigator.clipboard.writeText(link);
                            addAlert("Link copied to clipboard", "success");
                          }}
                          className="text-sm text-[#3B0069] flex items-center gap-1 hover:underline"
                        >
                          <Copy size={14} />
                          Copy Link
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full h-[80vh] overflow-y-scroll max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Share RFQ Link
              </h3>
              <button
                onClick={handleCloseShareModal}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {!invitationLink ? (
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setShowShareModal(false);
                      setShowMultipleVendorsInvite(true);
                    }}
                    className="w-full px-4 py-2 mb-4 text-sm border-2 border-[#3B0069] text-[#3B0069] rounded-md hover:bg-[#F5EEF9] flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Send to Multiple Vendors
                  </button>
                  {/* Option to Select Existing Vendor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Existing Vendor (Optional)
                    </label>
                    <select
                      value={selectedExistingVendor?.id || ""}
                      onChange={(e) => {
                        const vendorId = e.target.value;
                        if (!vendorId) {
                          setSelectedExistingVendor(null);
                          setIsExistingVendor(false);
                          setShareFormData((prev) => ({
                            ...prev,
                            vendor_whatsapp_number: "",
                            vendor_name: "",
                            vendor_address: "",
                          }));
                          return;
                        }
                        const vendor = vendors.find(
                          (v) => v.id === parseInt(vendorId)
                        );
                        if (vendor) {
                          setSelectedExistingVendor(vendor);
                          setIsExistingVendor(true);
                          setShareFormData((prev) => ({
                            ...prev,
                            vendor_whatsapp_number: vendor.contact_no.replace(
                              /^\+91/,
                              ""
                            ),
                            vendor_name: vendor.name || "",
                            vendor_address: vendor.address || "",
                          }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                    >
                      <option value="">
                        -- Select vendor or enter manually --
                      </option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name} ({v.contact_no})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* WhatsApp Number Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor WhatsApp Number{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-row items-center gap-3">
                      <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                        +91
                      </div>
                      <input
                        type="text"
                        placeholder="Enter vendor WhatsApp number"
                        value={shareFormData.vendor_whatsapp_number}
                        onChange={(e) =>
                          setShareFormData({
                            ...shareFormData,
                            vendor_whatsapp_number: e.target.value
                              .replace(/[^\d]/g, "")
                              .slice(0, 10),
                          })
                        }
                        disabled={isExistingVendor}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] ${isExistingVendor
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "border-gray-300"
                          }`}
                      />
                    </div>
                    {isExistingVendor && (
                      <p className="text-xs text-green-600 mt-1">
                        Matched with existing vendor:{" "}
                        {selectedExistingVendor?.name}
                      </p>
                    )}
                  </div>

                  {/* Vendor Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter vendor name"
                      value={shareFormData.vendor_name}
                      onChange={(e) =>
                        setShareFormData({
                          ...shareFormData,
                          vendor_name: e.target.value,
                        })
                      }
                      disabled={isExistingVendor}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] ${isExistingVendor
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : "border-gray-300"
                        }`}
                    />
                  </div>

                  {/* Vendor Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Address (Optional)
                    </label>
                    <textarea
                      placeholder="Enter vendor address"
                      value={shareFormData.vendor_address}
                      onChange={(e) =>
                        setShareFormData({
                          ...shareFormData,
                          vendor_address: e.target.value,
                        })
                      }
                      rows={3}
                      disabled={isExistingVendor}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] ${isExistingVendor
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : "border-gray-300"
                        }`}
                    />
                  </div>

                  {/* Validity Days */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link Validity (Days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={shareFormData.validity_days}
                      onChange={(e) =>
                        setShareFormData({
                          ...shareFormData,
                          validity_days: parseInt(e.target.value) || 7,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCloseShareModal}
                    className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleShareLink}
                    disabled={
                      isSubmitting ||
                      !shareFormData.vendor_whatsapp_number ||
                      !shareFormData.vendor_name
                    }
                    className="flex-1 px-4 py-2 text-sm bg-[#3B0069] text-white rounded-md hover:bg-[#2d0051] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Send Invitation"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 py-4">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Invitation link generated successfully! Share this link with
                    the vendor:
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <input
                      type="text"
                      value={invitationLink}
                      readOnly
                      className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                      title="Copy link"
                    >
                      {linkCopied ? (
                        <Check size={18} className="text-green-600" />
                      ) : (
                        <Copy size={18} className="text-gray-600" />
                      )}
                    </button>
                  </div>
                  {linkCopied && (
                    <p className="text-xs text-green-600 mt-2">
                      Link copied to clipboard!
                    </p>
                  )}
                </div>

                <button
                  onClick={handleCloseShareModal}
                  className="w-full px-4 py-2 text-sm bg-[#3B0069] text-white rounded-md hover:bg-[#2d0051]"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showFillVendor && (
        <FillVendor
          rfq={rfq}
          onClose={() => {
            setShowFillVendor(false);
            getQuotations();
          }}
        />
      )}
      {openReceivedQuotation && (
        <RecievedQuoatation
          quotation={openReceivedQuotation}
          rfq={rfq}
          onClose={() => {
            setOpenReceivedQuotation(null);
            getQuotations();
            onClose();
          }}
          onProceedToPO={onProceedToPO}
        />
      )}
      {showMultipleVendorsInvite && (
        <MultipleVendorsInvite
          rfq={rfq}
          vendors={vendors}
          onClose={() => setShowMultipleVendorsInvite(false)}
          onSuccess={() => {
            getInvitations();
            getQuotations();
          }}
          addAlert={addAlert}
        />
      )}
    </div>
  );
}
